import {m} from 'framer-motion';

import {jwtDecode} from 'jwt-decode';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import {Theme, SxProps, alpha} from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {useNavigate} from 'react-router-dom';

// import {paths} from 'src/routes/paths';
import {useRouter} from 'src/routes/hooks';

import {useAuthContext} from '@context/AuthContext';

import {useMsal} from '@azure/msal-react';
import {useIsAuthenticated} from '@azure/msal-react';

import {varHover} from 'src/minimal-components/animate';
import {useSnackbar} from 'src/minimal-components/snackbar';
import CustomPopover, {usePopover} from 'src/minimal-components/custom-popover';
import FormConstants from '@shared/constants/FormConstants';
import EncryptUtils from 'src/utils/EncryptUtils';
import AuthUtils from 'src/utils/AuthUtils';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {
  signOutUser,
  adfsLogOutService
} from '@shared/services/service-handlers/PrivateService';
import {Tooltip} from '@mui/material';
import {OAuthlogoutService} from '@shared/services/service-handlers/PublicService';
import {environment} from '@shared/config/environment';
import {useAppContext} from '@context/AppContext';
import {BASE_ROUTE} from 'src/routes/BaseRoute';
import {PUBLIC_ROUTES} from 'src/routes/RouteConstants';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Dashboard',
    linkTo: '/bytepad/dashboard'
  },
  // {
  //   label: 'Profile',
  //   linkTo: paths.dashboard.user.profile
  // },
  {
    label: 'Settings',
    linkTo: '/bytepad/module/settings'
  }
];
const OPTIONSLMS = [
  {
    label: 'Dashboard',
    linkTo: '/bytepad/dashboard'
  }
  // {
  //   label: 'Profile',
  //   linkTo: paths.dashboard.user.profile
  // // },
  // {
  //   label: 'Settings',
  //   linkTo: '/bytepad/module/settings'
  // }
];
// ----------------------------------------------------------------------

type Props = {
  sx?: SxProps<Theme>;
  isDashboard?: boolean;
};

export default function AccountPopover({sx, isDashboard}: Props) {
  const router = useRouter();
  const navigate = useNavigate();
  const {
    appState: {applicationName}
  } = useAppContext();
  let userData: any;

  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }

  const user = userData ? userData.user : null;

  const displayName =
    userData?.name ||
    userData?.user?.name ||
    userData?.user?.first_name + ' ' + userData?.user?.last_name;
  const email = userData?.user?.email || userData?.preferred_username;

  const {logout} = useAuthContext();

  const {enqueueSnackbar} = useSnackbar();

  const popover = usePopover();

  const isAzureAuthenticated = useIsAuthenticated();
  const {instance} = useMsal();

  const handleLogout = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS
        }
      };

      if (userData?.user?.type === FormConstants.LOGIN_TYPE.OAUTH) {
        const {redirectUrl}: any = await OAuthlogoutService({
          origin: window.location.origin
        });
        AuthUtils.clearStorage();
        window.location.href = redirectUrl;
        return;
      }

      if (userData?.user?.type === FormConstants.LOGIN_TYPE.ADFS) {
        await adfsLogOutService({origin: window.location.origin});
        popover.onClose();
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        return;
      }

      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData
      });

      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        instance.logoutRedirect({
          postLogoutRedirectUri: '/'
        });
        return;
      } else {
        logout();
      }

      popover.onClose();
      AuthUtils.clearStorage();
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 35,
          height: 35,
          background: theme => alpha(theme.palette.primary.lighter, 0.8),
          // background: theme => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: theme =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
          }),
          '&:hover': {
            background: theme =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
          },
          ...sx
        }}>
        <Tooltip
          title={'Account'}
          placement="bottom"
          enterDelay={100}
          leaveDelay={50}>
          <Avatar
            src={user?.photoURL}
            alt={displayName}
            sx={{
              width: 30,
              height: 30,
              border: theme => `solid 1px ${theme.palette.background.default}`,
              color: theme => theme.palette.common.white
            }}>
            {displayName?.charAt(0).toUpperCase()}
          </Avatar>
        </Tooltip>
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{width: 200, p: 0}}>
        <Box sx={{p: 2, pb: 1.5}}>
          <Typography variant="subtitle2" noWrap>
            {displayName}
          </Typography>

          <Typography variant="body2" sx={{color: 'text.secondary'}} noWrap>
            {email}
          </Typography>
        </Box>
        <Divider sx={{borderStyle: 'dashed'}} />

        {!isDashboard &&
          (environment.IS_LMS_ENABLED_FOR_EXTERNAL_SITE_AS_EMBEDDED &&
          applicationName === 'LMS' ? (
            <Stack sx={{p: 1}}>
              {OPTIONSLMS.map(option => (
                <MenuItem
                  key={option.label}
                  onClick={() => handleClickItem(option.linkTo)}>
                  {option.label}
                </MenuItem>
              ))}
            </Stack>
          ) : (
            <>
              <Stack sx={{p: 1}}>
                {OPTIONS.map(option => (
                  <MenuItem
                    key={option.label}
                    onClick={() => handleClickItem(option.linkTo)}>
                    {option.label}
                  </MenuItem>
                ))}
              </Stack>

              <Divider sx={{borderStyle: 'dashed'}} />
              <MenuItem
                onClick={handleLogout}
                sx={{
                  m: 1,
                  fontWeight: 'fontWeightBold',
                  color: 'error.main'
                }}>
                Logout
              </MenuItem>
            </>
          ))}
      </CustomPopover>
    </>
  );
}

